import { c as _c } from "react/compiler-runtime";
import { CdnImage } from '@cc-shopping/ui/primitives/CdnImage';
import { Box, type SxProps, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import type { StructuredContentDocument } from '../../types';
import type { AlignValue, SpacingValue } from './types';
import { CONTAINER_STYLE_MAPPER, SPACING_PROP_MAPPER } from './utils';
type ContentImageProps = StructuredContentDocument<{
  align: AlignValue | null;
  alt: string;
  backgroundColor?: string;
  caption: ReactNode;
  children?: ReactNode;
  fetchPriority?: 'high' | 'low' | 'auto';
  lazy?: boolean;
  naturalHeight: number;
  naturalWidth: number;
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
  objectPosition?: string;
  placeholderImage?: string;
  spacing?: {
    top?: SpacingValue;
    bottom?: SpacingValue;
  };
  src: string;
  title: string;
}>;
export function ContentImage(t0) {
  const $ = _c(45);
  const {
    props: t1
  } = t0;
  const {
    align: t2,
    alt,
    backgroundColor: t3,
    caption,
    fetchPriority,
    lazy,
    naturalHeight,
    naturalWidth,
    objectFit,
    objectPosition,
    placeholderImage,
    spacing: spacingProp,
    src,
    title
  } = t1;
  const align = t2 === undefined ? "none" : t2;
  const backgroundColor = t3 === undefined ? "transparent" : t3;
  let t4;
  if ($[0] !== spacingProp) {
    t4 = spacingProp?.top && {
      marginTop: SPACING_PROP_MAPPER[spacingProp.top]
    };
    $[0] = spacingProp;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== spacingProp) {
    t5 = spacingProp?.bottom && {
      marginBottom: SPACING_PROP_MAPPER[spacingProp.bottom]
    };
    $[2] = spacingProp;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== t4 || $[5] !== t5) {
    t6 = {
      ...t4,
      ...t5
    };
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const containerSpacing = t6;
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      display: "flex",
      flexDirection: "column",
      margin: 0
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const t8 = align && CONTAINER_STYLE_MAPPER[align];
  let t9;
  if ($[8] !== containerSpacing || $[9] !== t8) {
    t9 = {
      ...t8,
      ...containerSpacing
    };
    $[8] = containerSpacing;
    $[9] = t8;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== align) {
    t10 = align !== "none" && {
      marginLeft: "auto",
      marginRight: "auto"
    };
    $[11] = align;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== placeholderImage) {
    t11 = placeholderImage && {
      backgroundImage: `url("${placeholderImage}")`
    };
    $[13] = placeholderImage;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t11) {
    t12 = {
      backgroundSize: "cover",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "top center",
      ...t11
    };
    $[15] = t11;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  const t13 = lazy ? "lazy" : "eager";
  let t14;
  if ($[17] !== objectFit || $[18] !== objectPosition) {
    t14 = {
      display: "block",
      width: "100%",
      height: "100%",
      top: 0,
      left: 0,
      objectPosition,
      objectFit
    };
    $[17] = objectFit;
    $[18] = objectPosition;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== alt || $[21] !== fetchPriority || $[22] !== naturalHeight || $[23] !== naturalWidth || $[24] !== src || $[25] !== t13 || $[26] !== t14 || $[27] !== title) {
    t15 = <CdnImage alt={alt} width={naturalWidth} height={naturalHeight} title={title} src={src} loading={t13} style={t14} fetchPriority={fetchPriority} />;
    $[20] = alt;
    $[21] = fetchPriority;
    $[22] = naturalHeight;
    $[23] = naturalWidth;
    $[24] = src;
    $[25] = t13;
    $[26] = t14;
    $[27] = title;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== backgroundColor || $[30] !== t12 || $[31] !== t15) {
    t16 = <Box flex={1} overflow="hidden" bgcolor={backgroundColor} sx={t12}>{t15}</Box>;
    $[29] = backgroundColor;
    $[30] = t12;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== caption) {
    t17 = caption && <figcaption style={{
      flex: "none"
    }}><Typography color="neutral.light" variant="bodySmall" sx={{
        whiteSpace: "pre-wrap"
      }}>{caption}</Typography></figcaption>;
    $[33] = caption;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== t16 || $[36] !== t17) {
    t18 = <Box display="flex" flexDirection="column" margin={0}>{t16}{t17}</Box>;
    $[35] = t16;
    $[36] = t17;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== naturalWidth || $[39] !== t10 || $[40] !== t18) {
    t19 = <Box width={naturalWidth} maxWidth="100%" {...t10}>{t18}</Box>;
    $[38] = naturalWidth;
    $[39] = t10;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== t19 || $[43] !== t9) {
    t20 = <figure style={t7}><Box sx={t9}>{t19}</Box></figure>;
    $[42] = t19;
    $[43] = t9;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  return t20;
}